import * as React from 'react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import { MarkdownContent } from '../markdown-content';
import type { ViewerSourceEditing } from './TextEditorView';
import { ViewEditToggle } from './ViewEditToggle';
import { LoadingState } from '../shogun-spinner';

// Lazy: the CM6 editor is the heavy chunk. MarkdownView itself loads eagerly
// with FileViewerPane, so the editor import must stay dynamic — the raw side
// only pays for it when an editing host actually opens a markdown file.
const LazyTextEditorView = React.lazy(() =>
  import('./TextEditorView').then((m) => ({ default: m.TextEditorView })),
);

/**
 * Markdown viewer. Without an editable host (`editing` absent or read-only)
 * it only renders. With one, a View/Edit switch shows the CM6 editor, and
 * `content` is the buffer AT SYNC POINTS — so the rendered view reflects
 * saved-or-synced edits, never disk. Dirty pins the switch to Edit with a
 * "save to preview" hint.
 */
export function MarkdownView({
  content,
  filename,
  editing,
}: {
  content: string;
  /** Required for language selection when `editing` hosts the source editor. */
  filename?: string;
  editing?: ViewerSourceEditing;
}) {
  const [localEditMode, setLocalEditMode] = useState(false);
  const editable = editing != null && !editing.readOnly;
  const hosted = editing?.editMode !== undefined;
  const editMode = editing?.editMode ?? localEditMode;
  const setEditMode = editing?.onEditModeChange ?? setLocalEditMode;
  // Dirty pins the switch to Edit: unsaved edits must stay visible in
  // the surface that owns them until saved.
  const pinnedToEdit = editing?.dirty === true;
  const showEditor = editable && (pinnedToEdit || editMode);

  return (
    <div className={cn('rounded-md text-body', editing && 'flex h-full min-h-0 flex-col')}>
      {editable && !hosted && (
        <div className="flex items-center justify-end gap-1 border-b px-3 py-1.5">
          {pinnedToEdit && (
            <span className="mr-auto text-caption text-muted-foreground" data-testid="save-to-preview-hint">
              Save to preview
            </span>
          )}
          <ViewEditToggle editing={showEditor} onChange={setEditMode} disabled={pinnedToEdit} />
        </div>
      )}
      <div className={cn('p-3', editing && 'min-h-0 flex-1 overflow-auto')}>
        {showEditor ? (
          <React.Suspense fallback={<LoadingState label="Loading editor…" />}>
            <LazyTextEditorView
              content={content}
              filename={filename ?? 'file.md'}
              readOnly={false}
              onDocChanged={editing.onDocChanged}
              getTextRef={editing.getTextRef}
              onSave={editing.onSave}
              className="h-full"
            />
          </React.Suspense>
        ) : (
          <MarkdownContent content={content} />
        )}
      </div>
    </div>
  );
}
